import type { TraceErrorObject } from 'owa-analytics-types';
import sleep from 'owa-sleep';
import type MailboxRequestOptions from './MailboxRequestOptions';
import type { InternalRequestOptions, RequestOptions, RetryOptions } from './RequestOptions';
import addFetchRetryHeaders from './addFetchRetryHeaders';
import { resetOwaCanaryCookie } from './canary';
import type { ServiceRequestConfig } from './config';
import { HEADER_CORRELATIONID, HEADER_WEBSSESSIONTYPE } from './constants/CommonHeaders';
import { AuthorizationHeaderName } from './createDefaultHeader';
import isAuthRetriableForRequest from './isAuthRetriableForRequest';
import isRetriableAuthError from './isRetriableAuthError';
import { isRetriableStatusCode } from './isRetriableStatusCode';

export type OnRetry = () => Promise<void>;

export interface RetryStrategy {
    shouldRetry: boolean;
    retryEndpoint?: string;
    onRetry?: OnRetry;
}

const defaultOwsApiRetryStrategy = {
    authErrorRetryDelayInMs: 10,
    defaultRetryDelayInMs: 100,
    networkErrorRetryDelayInMs: 5000,
    transientErrorRetryDelayInMs: 500,
    throttledErrorRetryDelayInMs: 2000,
};

export async function getRetryStrategy(
    err: TraceErrorObject | undefined,
    callCount: number,
    response: Response | undefined,
    config: ServiceRequestConfig,
    requestOptions: RequestOptions | MailboxRequestOptions | undefined,
    options: InternalRequestOptions
): Promise<RetryStrategy> {
    const {
        authErrorRetryDelayInMs,
        defaultRetryDelayInMs,
        networkErrorRetryDelayInMs,
        transientErrorRetryDelayInMs,
    } = config.getApplicationSettings?.('OwsApiRetryStrategy') || defaultOwsApiRetryStrategy;

    if (err) {
        return networkErrorRetry(err, callCount, options, networkErrorRetryDelayInMs);
    }

    // response should be defined if there is no error
    if (!response) {
        throw new Error('fetchWithRetry: No response returned from doFetch.');
    }

    const status = response.status;
    const resultType = response.headers?.get('x-owa-resulttype');

    if (
        options?.shouldRetry
            ? true === (await options.shouldRetry(status, response))
            : resultType
              ? resultType !== 'Success'
              : isRetriableStatusCode(status)
    ) {
        let retryOptions: RetryOptions | undefined;

        if (options?.onBeforeRetry) {
            retryOptions = await options.onBeforeRetry(response);

            if (!retryOptions?.endpoint) {
                return noRetry();
            }
        }

        let retryStrategy: RetryStrategy;

        if (isRetriableAuthError(status) && isAuthRetriableForRequest(options)) {
            retryStrategy = await authErrorRetry(
                callCount,
                requestOptions,
                options,
                config,
                response,
                authErrorRetryDelayInMs
            );
        } else {
            switch (resultType) {
                case 'ClientError':
                case 'ServerError':
                case 'Success':
                // Surface Throttled responses without issuing another request.
                case 'Throttled':
                    retryStrategy = noRetry();
                    break;
                case 'ServerTransientError':
                    retryStrategy = transientErrorRetry(
                        callCount,
                        options,
                        transientErrorRetryDelayInMs
                    );
                    break;
                default:
                    retryStrategy = defaultRetry(callCount, options, defaultRetryDelayInMs);
            }
        }

        return {
            retryEndpoint: retryOptions?.endpoint?.toString(),
            ...retryStrategy,
        };
    }

    return noRetry();
}

function noRetry(): RetryStrategy {
    return {
        shouldRetry: false,
    };
}

function defaultRetry(
    callCount: number,
    options: InternalRequestOptions,
    defaultRetryDelayInMs: number
): RetryStrategy {
    return {
        shouldRetry: callCount <= (options?.retryCount ?? 1),
        onRetry: async () => {
            await runAtRetry(callCount, options, defaultRetryDelayInMs);
        },
    };
}

async function authErrorRetry(
    callCount: number,
    requestOptions: RequestOptions | MailboxRequestOptions | undefined,
    options: InternalRequestOptions,
    config: ServiceRequestConfig,
    response: Response,
    authErrorRetryDelayInMs: number
): Promise<RetryStrategy> {
    // we should clear the auth token since we know it failed to authenticate
    config.onAuthFailed?.(response.headers, requestOptions?.mailboxInfo);
    const authToken = await config.getAuthToken?.(
        response.headers,
        requestOptions?.mailboxInfo,
        options.headers.get(HEADER_CORRELATIONID) ||
            options.headers.get('client-request-id') ||
            undefined
    );

    // if token is available, lets retry. otherwise handle the error and do auth redirect
    if (!authToken) {
        if (options.headers.has(AuthorizationHeaderName)) {
            options.headers.delete(AuthorizationHeaderName);
        }

        // bail out of retry since we cannot get a new auth token
        return noRetry();
    }

    return {
        shouldRetry: callCount <= (options?.retryCount ?? 1),
        onRetry: async () => {
            options.headers.set(AuthorizationHeaderName, authToken);

            if (config.getWebSessionType) {
                const webSessionType = await config.getWebSessionType();
                if (webSessionType) {
                    options.headers.set(HEADER_WEBSSESSIONTYPE, webSessionType);
                }
            }

            await runAtRetry(callCount, options, authErrorRetryDelayInMs);
        },
    };
}

function networkErrorRetry(
    err: TraceErrorObject,
    callCount: number,
    options: InternalRequestOptions,
    networkErrorRetryDelayInMs: number
): RetryStrategy {
    return {
        shouldRetry: err.retriable === true && callCount <= (options?.retryCount ?? 1),
        onRetry: async () => {
            await runAtRetry(callCount, options, networkErrorRetryDelayInMs);
        },
    };
}

function transientErrorRetry(
    callCount: number,
    options: InternalRequestOptions,
    transientErrorRetryDelayInMs: number
): RetryStrategy {
    return {
        shouldRetry: callCount <= (options?.retryCount ?? 3),
        onRetry: async () => {
            await runAtRetry(callCount, options, transientErrorRetryDelayInMs);
        },
    };
}

async function runAtRetry(callCount: number, options: InternalRequestOptions, baseDelayMs: number) {
    // on all retries, lets clear the canary to be safe
    resetOwaCanaryCookie();
    addFetchRetryHeaders(callCount, options?.headers);
    await sleep(baseDelayMs * 2 ** (callCount - 1));
}
